import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Landmark, Loader2, Play } from 'lucide-react';
import { CompanyStatusBadge } from '../CompanyStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RegistryPanel = () => {
  const [state, setState] = useState(null);
  const [logs, setLogs] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [s, l, f] = await Promise.all([
        axios.get(`${API}/admin/registry/state`, { withCredentials: true }),
        axios.get(`${API}/admin/registry/logs?limit=30`, { withCredentials: true }),
        axios.get(`${API}/admin/registry/flagged`, { withCredentials: true }),
      ]);
      setState(s.data); setLogs(l.data); setFlagged(f.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/admin/registry/check`, {}, { withCredentials: true });
      toast.success('Contrôle BCE lancé en arrière-plan (résultats dans quelques minutes)');
      setTimeout(load, 8000);
    } catch (e) { toast.error('Erreur lancement'); } finally { setRunning(false); }
  };

  return (
    <Card data-testid="registry-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Landmark className="w-5 h-5 text-[#1E3A8A]" /> Contrôle BCE des entreprises (15 & 30 du mois)</CardTitle>
        <Button size="sm" variant="outline" onClick={runNow} disabled={running} data-testid="registry-run-btn">
          {running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} Lancer maintenant
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="bg-[#F9FAFB] rounded-lg p-3"><p className="text-xs text-[#6B7280]">Entreprises suivies</p><p className="text-xl font-bold" data-testid="registry-tracked">{state?.tracked_companies ?? '—'}</p></div>
          <div className="bg-[#F9FAFB] rounded-lg p-3"><p className="text-xs text-[#6B7280]">Signalées</p><p className="text-xl font-bold text-red-600" data-testid="registry-flagged">{state?.flagged ?? '—'}</p></div>
          <div className="bg-[#F9FAFB] rounded-lg p-3"><p className="text-xs text-[#6B7280]">Dernier contrôle</p><p className="text-sm font-semibold" data-testid="registry-last-run">{state?.last_run ? new Date(state.last_run).toLocaleString('fr-BE') : 'Jamais'}</p></div>
          <div className="bg-[#F9FAFB] rounded-lg p-3"><p className="text-xs text-[#6B7280]">Résultat</p><p className="text-sm font-semibold">{state?.last_run ? `${state.checked} vérifiées · ${state.changed} changement(s) · ${state.errors} erreur(s)` : '—'}</p></div>
        </div>

        {flagged.length > 0 && (
          <div>
            <p className="text-sm font-semibold mb-2">Entreprises signalées</p>
            <div className="flex flex-wrap gap-2">
              {flagged.map((u) => (
                <div key={u.id} className="flex items-center gap-2 text-xs bg-white border rounded-md px-2 py-1" data-testid={`flagged-${u.id}`}>
                  <span className="font-medium">{u.company_name}</span><span className="text-[#9CA3AF]">{u.email}</span><CompanyStatusBadge status={u.kbo_status} compact />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Entreprise</TableHead><TableHead>Changements</TableHead><TableHead>Situation</TableHead></TableRow></TableHeader>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-[#9CA3AF]">Aucun changement détecté pour le moment</TableCell></TableRow>
              ) : logs.map((l) => (
                <TableRow key={l.id} data-testid={`registry-log-${l.id}`}>
                  <TableCell className="text-xs text-[#6B7280] whitespace-nowrap">{new Date(l.created_at).toLocaleString('fr-BE')}</TableCell>
                  <TableCell className="text-sm"><p className="font-medium">{l.company_name}</p><p className="text-xs text-[#9CA3AF]">{l.email}</p></TableCell>
                  <TableCell className="text-xs">{l.changes.map((c, i) => <p key={i}><span className="text-[#6B7280]">{c.label} :</span> <span className="line-through text-[#9CA3AF]">{c.old || '—'}</span> → <span className="font-medium">{c.new || '—'}</span></p>)}</TableCell>
                  <TableCell>{l.new_status?.level === 'none' ? <span className="text-xs text-emerald-700">Normale</span> : <CompanyStatusBadge status={l.new_status} compact />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
